import env from '~/libs/env'
import { computed, readonly } from 'vue'
import { storeToRefs } from 'pinia'
import { useBorrowerStore } from '~/store/borrower'
import { useUserStore } from '~/store/user'
import { localStorageService } from '~/libs/local-storage.service'

const SESSION_TTL_MS = 30 * 60 * 1000 // 30 minutes

/**
 * Panel display states, in order of prominence.
 */
export const PANEL_EXPANDED = 'expanded'
export const PANEL_COLLAPSED = 'collapsed'
export const PANEL_MINIMIZED = 'minimized'

function _sessionKey(agentKey, borrowerId) {
  return `${agentKey}_session_${borrowerId}`
}

function _panelKey(agentKey, borrowerId) {
  return `${agentKey}_panel_${borrowerId}`
}

/**
 * Data layer for a borrower-portal agent chat.
 * Agent-agnostic: the key picks the endpoints + storage namespace.
 *
 * @param {string} agentKey e.g. 'merchant-interview'
 */
export function useBorrowerAgent(agentKey) {
  const { $axios } = useNuxtApp()
  const borrowerStore = useBorrowerStore()
  const userStore = useUserStore()
  const { getBorrowerId } = storeToRefs(borrowerStore)

  /*
   * ( STATE )
  */
  const borrowerId = computed(() => getBorrowerId.value)

  // resolved once per visit per borrower — the panel lives on every page
  let eligibilityPromise = null
  let eligibilityForId = null

  /*
   * ( FUNCTIONS )
   * Session history lives in localStorage so the conversation survives reloads + navigation.
  */
  function getStoredSession(id) {
    try {
      const raw = localStorageService.getItem(_sessionKey(agentKey, id))
      if (!raw) return null
      const session = JSON.parse(raw)
      if (!session.savedAt || Date.now() - session.savedAt > SESSION_TTL_MS) {
        localStorageService.removeItem(_sessionKey(agentKey, id))
        return null
      }
      return session
    } catch {
      return null
    }
  }

  function saveSession(id, chatSessionId, messages, completed = false) {
    try {
      localStorageService.setItem(
        _sessionKey(agentKey, id),
        JSON.stringify({ chatSessionId, messages, completed, savedAt: Date.now() })
      )
    } catch {
      // storage quota exceeded or private mode — chat continues w/out persistence
    }
  }

  function clearSession(id) {
    try {
      localStorageService.removeItem(_sessionKey(agentKey, id))
    } catch {
      // blocked storage throws on access — nothing to clear, chat continues w/out persistence
    }
  }

  /**
   * The panel's display state, remembered across navigation.
   * Shares the session TTL — an expired conversation has nothing to reopen onto.
   */
  function getStoredPanelState(id) {
    try {
      const raw = localStorageService.getItem(_panelKey(agentKey, id))
      if (!raw) return null
      const stored = JSON.parse(raw)
      if (!stored.savedAt || Date.now() - stored.savedAt > SESSION_TTL_MS) {
        localStorageService.removeItem(_panelKey(agentKey, id))
        return null
      }
      return stored.state ?? null
    } catch {
      return null
    }
  }

  function savePanelState(id, state) {
    try {
      localStorageService.setItem(
        _panelKey(agentKey, id),
        JSON.stringify({ state, savedAt: Date.now() })
      )
    } catch {
      // non-fatal — the panel just reverts to its default state on the next page
    }
  }

  /*
   * ( PRIVATE FUNCTIONS )
  */
  function _getAuthHeader() {
    const authHeader = userStore.getAuthHeader({ useCookies: true })
    if (!authHeader) {
      throw new Error('No authentication header available')
    }
    return authHeader
  }

  function _agentUrl(id, endpoint) {
    return `${env('apiUrl')}/borrower-portal/borrower/${id}/agent/${agentKey}/${endpoint}`
  }

  /*
   * ( API )
  */

  /**
   * Whether the agent may run, and which path template it would run on.
   * `templateAlias` returns even when ineligible — it picks the invitation copy.
   * `anchorFacts` personalizes the invitation (firstName, amountSeeking, useOfFunds).
   *
   * @returns {Promise<{eligible: boolean, templateAlias: string|null, anchorFacts: object|null}>}
   */
  async function checkEligibility(id) {
    try {
      const { data: body } = await $axios.get(_agentUrl(id, 'eligibility'), {
        headers: { Authorization: _getAuthHeader() },
      })

      return {
        eligible: body?.data?.eligible === true,
        templateAlias: body?.data?.templateAlias ?? null,
        anchorFacts: body?.data?.anchorFacts ?? null,
      }
    } catch (error) {
      console.error(`[useBorrowerAgent:${agentKey}] checkEligibility error:`, error?.response?.status, error?.response?.data)
      throw error
    }
  }

  /**
   * Resolve eligibility at most once per visit.
   *
   * @returns {Promise<{eligible: boolean, templateAlias: string|null, anchorFacts: object|null}>}
   */
  function resolveEligibilityOnce(id) {
    // a business switch changes the borrower w/out remounting — never serve another borrower's verdict
    if (eligibilityForId !== id) {
      eligibilityPromise = null
      eligibilityForId = id
    }

    eligibilityPromise ??= checkEligibility(id).catch((error) => {
      // don't cache a failure — a transient error shouldn't disable the panel for the visit
      eligibilityPromise = null
      throw error
    })

    return eligibilityPromise
  }

  /**
   * Open a session w/ the borrower's first message — no session until they say something.
   * Idempotent: an already-active session returns its chatSessionId w/ `lastMessage: null`.
   */
  async function startSession(id, message) {
    try {
      const { data: body } = await $axios.post(_agentUrl(id, 'start'), { message }, {
        headers: { Authorization: _getAuthHeader() },
      })
      return body?.data ?? null
    } catch (error) {
      console.error(`[useBorrowerAgent:${agentKey}] startSession error:`, error?.response?.status, error?.response?.data)
      throw error
    }
  }

  /**
   * Send a follow-up turn. 409 (interview already complete) resolves as completion, not failure.
   */
  async function continueSession(id, chatSessionId, message) {
    try {
      const { data: body } = await $axios.post(_agentUrl(id, 'continue'), { chatSessionId, message }, {
        headers: { Authorization: _getAuthHeader() },
      })
      return body?.data ?? null
    } catch (error) {
      if (error?.response?.status === 409) {
        return { chatSessionId, lastMessage: null, complete: true }
      }
      console.error(`[useBorrowerAgent:${agentKey}] continueSession error:`, error?.response?.status, error?.response?.data)
      throw error
    }
  }

  return {
    // state
    borrowerId: readonly(borrowerId),
    // session persistence
    getStoredSession,
    saveSession,
    clearSession,
    // panel persistence
    getStoredPanelState,
    savePanelState,
    // api
    checkEligibility,
    resolveEligibilityOnce,
    startSession,
    continueSession,
  }
}
